import {
  FetchArgs,
  createApi,
  BaseQueryFn,
  fetchBaseQuery,
  FetchBaseQueryError,
} from '@reduxjs/toolkit/query/react'
import { debounce } from 'lodash'

import { AnyAction } from '@reduxjs/toolkit'
import { HYDRATE } from 'next-redux-wrapper'
import type { CreateApiOptions } from '@reduxjs/toolkit/query/react'
import type { FetchBaseQueryArgs } from '@reduxjs/toolkit/query/react'

import cognito from './config/cognito'
import { Project } from './project'
import { getStrings } from './strings'
import { REHYDRATE } from 'redux-persist'
import { getStore, StoreStateType } from './store'
import { Constants } from './config/constants'

declare const USE_PROXY: boolean | undefined

export function extractRehydrationInfo(
  action: AnyAction,
  {
    reducerPath,
  }: {
    reducerPath: string
  },
) {
  if (action.type === HYDRATE) {
    return action.payload[reducerPath]
  }
}

let q: BaseQueryFn<string | FetchArgs, unknown, FetchBaseQueryError> | null =
  null

const debouncedLogout = debounce(
  () => {
    // @ts-expect-error - logout endpoint is dynamically added via service injection
    getStore().dispatch(service.endpoints.logout.initiate({}, {}))
  },
  500,
  { leading: true },
)

export const baseApiOptions = (queryArgs?: Partial<FetchBaseQueryArgs>) => {
  const baseQuery: BaseQueryFn<
    string | FetchArgs,
    unknown,
    FetchBaseQueryError
  > = async (args, api, extraOptions) => {
    const _baseQuery = q
      ? q
      : fetchBaseQuery({
          baseUrl:
            typeof USE_PROXY !== 'undefined' && USE_PROXY
              ? '/api/proxy'
              : Project.api,
          fetchFn: async (args: any) => {
            if (typeof USE_PROXY !== 'undefined' && USE_PROXY) {
              args.headers.append('originalurl', args.url)
            }
            const result = await fetch(args)
            const isUnauthorised = result?.status === 401
            if (isUnauthorised) {
              debouncedLogout()
            }

            return result
          },
          prepareHeaders: async (headers, { endpoint, getState }) => {
            const state = getState() as StoreStateType
            Object.entries(Project.headers).forEach(([key, value]) => {
              headers.set(key, String(value))
            })
            if (
              endpoint !== 'register' &&
              endpoint !== 'createConfirmEmail' &&
              endpoint !== 'createResetPassword' &&
              endpoint !== 'createResendConfirmationEmail' &&
              endpoint !== 'verifyAccount' &&
              endpoint !== 'createForgotPassword'
            ) {
              try {
                const session = await cognito.getSession()
                const jwtToken = session!.getAccessToken()
                const token = jwtToken?.getJwtToken()
                const testData = state?.devSettings?.testData
                if (testData) {
                  headers.set('x-is-test-request', `True`)
                }
                if (token) {
                  headers.set('Authorization', `Bearer ${token}`)
                }
              } catch (e) {}
            }
            const tenancy = state?.tenancy
            if (tenancy?.id) {
              headers.set('Tenant-ID-Authentication', `${tenancy.id}`)
            }
            headers.set('Accept-Language', getStrings().getLanguage())
            if (Constants.E2E) {
              headers.set('E2E-Test', '1')
            }
            return headers
          },
          ...queryArgs,
        })
    q = _baseQuery
    return _baseQuery(args, api, extraOptions)
  }

  const res: Pick<
    CreateApiOptions<any, any, any, any>,
    | 'baseQuery'
    | 'keepUnusedDataFor'
    | 'refetchOnReconnect'
    | 'refetchOnFocus'
    | 'extractRehydrationInfo'
  > = {
    baseQuery,
    extractRehydrationInfo: (
      action: AnyAction,
      { reducerPath }: { reducerPath: string },
    ) => {
      if (action.type === HYDRATE) {
        return action.payload[reducerPath]
      }
    },
    keepUnusedDataFor: 10,
    refetchOnFocus: true,
    refetchOnReconnect: true,
  }
  return res
}

const transformQuery = (query: any | undefined | null) => {
  if (!query?.data) {
    // if the query had data, treat it as cache
    return null
  }
  return {
    ...query,
    fulfilledTimeStamp: null,
    status: 'fulfilled',
  }
}
export const service = createApi({
  ...baseApiOptions(),
  endpoints: () => ({}),
  extractRehydrationInfo(
    action: AnyAction,
    { reducerPath }: { reducerPath: string },
  ) {
    if (
      (action.type === REHYDRATE ||
        action.type === '__NEXT_REDUX_WRAPPER_HYDRATE__') &&
      action.payload?.[reducerPath]
    ) {
      const queries: Record<string, any> = {}
      const payloadQueries = action.payload[reducerPath]?.queries as
        | Record<string, any>
        | undefined
      if (payloadQueries) {
        Object.keys(payloadQueries).forEach((key) => {
          queries[key] = transformQuery(payloadQueries[key])
        })
      }
      return {
        'config': {},
        'mutations': {},
        'provided': {},
        'queries': queries,
        'subscriptions': {},
      } as any
    }
  },
  keepUnusedDataFor: 10,
  reducerPath: 'service',
})
